import { useEffect, useState, type ReactNode } from 'react'
import { Button, Chip, Input, Switch } from '@heroui/react'
import { api, type StoredLayout } from '../lib/api'
import { listState } from '../lib/listState'
import { nameIn } from '../lib/staffText'
import type { DeliveredService, Queue, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { DeliveredTree } from './Delivered'
import { Loading } from './Loading'
import { Check, Messages, Names, Num, Text, activeFirst, useAction } from './parts'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, PageHead, RowArrow, toneOf, useOpenRecord } from './settingsKit'
import { EmptyState } from '../ui/parts'
import { useMayRead, useSession } from '../ui/Session'
import { optionOn } from '../lib/orgOptions'
import '../styles/branch.css'
import { Pick as SetupPick } from './setupKit'

/**
 * Services: what the customer came to do.
 *
 * A service belongs to the organisation rather than to a branch. "Cash" means the
 * same thing in every branch of a bank, and a branch that does not offer it simply
 * routes it nowhere — which is a decision made on the queues screen. So the list
 * marks a service no queue in the branch chosen in the top bar takes: it is on
 * offer, and nobody there can get a ticket for it.
 *
 * The ticket letter and the waiting target belong to the queues of the operation
 * profile, not to the service; the list shows the letters of the branch's queues
 * that take it.
 *
 * Under each service, in its panel, is the tree of what a desk may say it
 * delivered within it — see Delivered.tsx.
 */

const blank = (): Partial<Service> => ({
  code: '',
  name: {},
  avgServiceSec: 300,
  sortOrder: 0,
  active: true,
})

export function Services({
  organisation,
  branchId,
  branchName,
}: {
  organisation?: string
  /** The branch in the top bar, whose queues say which services it routes. */
  branchId?: string
  branchName?: string
}) {
  const { lang, say } = useStaffText()
  const handoffOn = optionOn(useSession()?.identity, 'handoff')
  const [services, setServices] = useState<Service[]>([])
  const [delivered, setDelivered] = useState<DeliveredService[]>([])
  const [queues, setQueues] = useState<Queue[] | null>(null)
  const [find, setFind] = useState('')
  const [kind, setKind] = useState<'all' | 'on' | 'off'>('all')
  // False until the first answer, so a slow line reads as Loading, not as empty.
  const [loaded, setLoaded] = useState(false)
  // The same for the tree of delivered services, which is read apart from the list.
  const [treeLoaded, setTreeLoaded] = useState(false)

  // The organisation's ticket designs. A failure is not worth a red line: the
  // chooser then offers only the branch's slip, which is what it says.
  const [tickets, setTickets] = useState<StoredLayout[]>([])
  const readsLayouts = useMayRead('layouts')
  useEffect(() => {
    if (!readsLayouts) return
    api
      .layouts(organisation)
      .then((r) => setTickets(r.layouts.filter((l) => l.kind === 'ticket')))
      .catch(() => setTickets([]))
  }, [organisation, readsLayouts])

  // The branch's queues, only for the "No queue" mark. Unknown is not "none":
  // without an answer nothing is marked.
  useEffect(() => {
    setQueues(null)
    if (!branchId) return
    api
      .queues(branchId)
      .then((r) => setQueues(r.queues ?? []))
      .catch(() => setQueues(null))
  }, [branchId])

  const load = async () => {
    // The tree is caught on its own: a failure to read it must not turn a service
    // that saved into a red line, nor leave the services list stale.
    const [listed, tree] = await Promise.all([
      api.catalogueServices(organisation),
      api.deliveredServices(organisation).catch(() => undefined),
    ])
    setServices(listed.services)
    if (tree) setDelivered(tree.deliveredServices)
    setLoaded(true)
    setTreeLoaded(true)
  }
  const { busy, error, notice, run, setError } = useAction(load)

  useEffect(() => {
    api
      .catalogueServices(organisation)
      .then((listed) => {
        setServices(listed.services)
        setLoaded(true)
      })
      .catch(() => {
        setError(say('catalogue.services.notLoaded'))
        setLoaded(true)
      })
    // Apart from the services, so that a server without the tree still shows the
    // services it can.
    api
      .deliveredServices(organisation)
      .then((tree) => setDelivered(tree.deliveredServices))
      .catch(() => setDelivered([]))
      .finally(() => setTreeLoaded(true))
  }, [organisation, setError, say])

  const f = find.trim().toLocaleLowerCase()
  const shown = activeFirst(services, (s) => s.active).filter(
    (s) => !f || `${nameIn(s.name, lang, '')} ${s.code}`.toLocaleLowerCase().includes(f),
  )
  const routed = (s: Service) => queues?.some((q) => q.active && (q.serviceIds ?? []).includes(s.id)) ?? true
  const letters = (s: Service) => [
    ...new Set(
      (queues ?? [])
        .filter((q) => q.active && (q.serviceIds ?? []).includes(s.id))
        .map((q) => q.letter || q.code.slice(0, 1)),
    ),
  ]
  // The service open on its own page (#services/<id>, #services/new), as the list now
  // has it: the delivered tree under it is saved row by row and read back into this list.
  const rec = useOpenRecord(services, (s) => s.id, loaded)
  const open = rec.row
  const editing = rec.isNew ? blank() : open

  const offered = services.filter((s) => s.active).length
  const listed = shown.filter((s) => kind === 'all' || (kind === 'on') === s.active)

  if (editing) {
    return (
      <>
        <ServiceSheet
          key={rec.code}
          service={editing}
          saved={open}
          tickets={tickets}
          busy={busy}
          error={error}
          onClose={() => {
            rec.close()
            setError('')
          }}
          onSave={(draft) =>
            editing.id
              ? run(async () => {
                  await api.updateService(editing.id!, draft)
                  rec.close()
                }, say('catalogue.services.saved', { name: nameIn(draft.name, lang, draft.code ?? '') }))
              : run(async () => {
                  await api.createService({ ...draft, ...(organisation ? { organisation } : {}) })
                  rec.close()
                }, say('catalogue.services.added'))
          }
        >
          {open && (
            <DeliveredTree
              service={open}
              rows={delivered.filter((d) => d.serviceId === open.id)}
              loaded={treeLoaded}
              busy={busy}
              run={run}
            />
          )}
        </ServiceSheet>
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.services')}
        line={say('settings.services.line')}
        actions={
          <Button variant="primary" onPress={() => rec.open()}>
            <Icon name="plus" size={16} /> {say('catalogue.services.add')}
          </Button>
        }
      />
      <Messages error={error} notice={notice} />

      <ListCard
        title={say('admin.section.services')}
        count={loaded ? say('settings.services.count', { n: services.length }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('catalogue.services.find')}
        chips={[
          { value: 'all', label: say('settings.all') },
          { value: 'on', label: say('catalogue.services.offered'), count: offered },
          { value: 'off', label: say('settings.services.off'), count: services.length - offered },
        ]}
        chip={kind}
        onChip={setKind}
      >
        {listState(loaded, services.length) === 'loading' && <Loading />}
        {listState(loaded, services.length) === 'empty' && (
          <EmptyState icon={<Icon name="grid" size={20} />} title={say('catalogue.services.nothingOffered')} />
        )}
        {services.length > 0 && listed.length === 0 && <EmptyState title={say('catalogue.services.noMatch', { q: find })} />}
        {listed.length > 0 && (
          <ListTable
            label={say('admin.section.services')}
            cols="minmax(220px, 1.6fr) minmax(90px, .7fr) 110px 76px 16px"
            head={[
              say('catalogue.services.service'),
              say('catalogue.services.letter'),
              say('catalogue.services.usual'),
              say('catalogue.services.offered'),
              '',
            ]}
          >
            {listed.map((s) => {
              const name = nameIn(s.name, lang, s.code)
              return (
                <ListRow key={s.id} actions off={!s.active} onOpen={() => rec.open(s)}>
                  <NameCell
                    tile={<IconTile text={s.code.slice(0, 2)} tone={toneOf(s.id)} />}
                    name={
                      <>
                        {name}
                        {s.active && !routed(s) && (
                          <Chip
                            size="sm"
                            variant="soft"
                            color="warning"
                            title={branchName ? say('catalogue.services.noQueueIn', { branch: branchName }) : undefined}
                          >
                            {say('catalogue.services.noQueue')}
                          </Chip>
                        )}
                        {handoffOn && s.cashier && (
                          <Chip size="sm" variant="secondary">
                            {say('catalogue.services.cashier')}
                          </Chip>
                        )}
                      </>
                    }
                    sub={s.code}
                  />
                  <span role="cell">
                    {letters(s).map((l) => (
                      <span key={l} className="stub stub--sm svc-stub">
                        {l}
                      </span>
                    ))}
                  </span>
                  <span role="cell" className="set-num">
                    {say('catalogue.minutes', { n: Math.round(s.avgServiceSec / 60) })}
                  </span>
                  <span role="cell">
                    <Switch
                      isSelected={s.active}
                      isDisabled={busy}
                      aria-label={say('catalogue.services.offer', { name })}
                      onChange={() =>
                        run(
                          () => api.updateService(s.id, { ...s, active: !s.active }),
                          s.active ? say('catalogue.services.nowOff', { name }) : say('catalogue.services.nowOn', { name }),
                        )
                      }
                    >
                      <Switch.Content>
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Content>
                    </Switch>
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>
    </>
  )
}

function ServiceSheet({
  service,
  saved,
  tickets,
  busy,
  error,
  onClose,
  onSave,
  children,
}: {
  service: Partial<Service>
  saved?: Service
  tickets: StoredLayout[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Partial<Service>) => void
  children?: ReactNode
}) {
  const handoffOn = optionOn(useSession()?.identity, 'handoff')
  const { lang, say } = useStaffText()
  const [draft, setDraft] = useState<Partial<Service>>(service)
  const isNew = !service.id
  const set = (patch: Partial<Service>) => setDraft((d) => ({ ...d, ...patch }))
  const valid = Boolean(draft.code?.trim()) && JSON.stringify(draft) !== JSON.stringify(service)
  // Minutes on screen, seconds on the wire; a field untouched keeps its seconds.
  const minutes = (sec: number | undefined) => Math.round((sec ?? 0) / 60)
  const toSec = (typed: string) => Math.max(0, Math.round(Number(typed) || 0)) * 60

  return (
    <InnerPage
      back={say('admin.section.services')}
      title={isNew ? say('catalogue.services.new') : nameIn(service.name, lang, service.code ?? '')}
      onClose={onClose}
      onSubmit={() => onSave(draft)}
      submitLabel={isNew ? say('catalogue.services.add') : say('catalogue.services.save')}
      valid={valid}
      busy={busy}
      error={error}
      wide={!isNew}
    >
      <Names value={draft.name ?? {}} onChange={(name) => set({ name })} />
      <div className="branch-pair admin-top">
        <label className="field">
          <span title={say('catalogue.services.codeHint')}>{say('common.code')}</span>
          <Text uppercase value={draft.code ?? ''} onChange={(code) => set({ code })} />
        </label>
        <label className="field">
          <span title={say('catalogue.services.handlingHint')}>{say('catalogue.services.usualMin')}</span>
          <Input
            fullWidth
            type="number"
            min={0}
            value={minutes(draft.avgServiceSec)}
            onChange={(e) => set({ avgServiceSec: toSec(e.target.value) })}
          />
        </label>
      </div>

      <label className="field">
        <span title={say('catalogue.services.limitHint')}>{say('catalogue.services.limitMin')}</span>
        <Input
          className="admin-num"
          type="number"
          min={0}
          max={480}
          value={draft.serviceTimeLimitMin ?? 0}
          onChange={(e) => set({ serviceTimeLimitMin: Math.max(0, Math.min(480, Math.round(Number(e.target.value) || 0))) })}
        />
      </label>

      {!isNew && (
        <>
          <label className="field">
            <span title={say('catalogue.lowerFirst')}>{say('catalogue.services.kioskOrder')}</span>
            <Num value={draft.sortOrder ?? 0} onChange={(sortOrder) => set({ sortOrder })} />
          </label>
          {/* Which slip this service prints. Optional: empty prints what the
              branch prints, which is almost every service. A kiosk with a slip of
              its own still prints that — the lounge keeps its paper. */}
          <label className="field">
            <span>{say('admin.branches.printedTicket')}</span>
            <SetupPick
              label={say('admin.branches.printedTicket')}
              value={draft.ticketLayoutId ?? ''}
              onChange={(ticketLayoutId) => set({ ticketLayoutId })}
              options={[
                { value: '', label: say('catalogue.services.whatBranchPrints') },
                ...tickets.map((layout) => ({
                  value: layout.id,
                  label: layout.published
                    ? layout.name || say('admin.branches.unnamedTicket')
                    : say('admin.branches.notPublishedYet', { name: layout.name || say('admin.branches.unnamedTicket') }),
                })),
              ]}
            />
          </label>
          <Check
            label={say('catalogue.services.offered')}
            checked={draft.active ?? true}
            onChange={(active) => set({ active })}
            hint={say('catalogue.services.offeredHint')}
          />
          {handoffOn && (
            <Check
              label={say('catalogue.services.cashier')}
              checked={draft.cashier ?? false}
              onChange={(cashier) => set({ cashier })}
              hint={say('catalogue.services.cashierHint')}
            />
          )}
          {saved && children}
        </>
      )}
    </InnerPage>
  )
}
